import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState, Fragment, useEffect } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import {
  Card,
  Grid,
  Divider,
  Checkbox,
  useTheme,
  TextField,
  CardHeader,
  Typography,
  CardContent,
  FormHelperText,
  FormControlLabel
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { RejectionReason } from 'src/services/applications/applications.types';
import { UserManagedRejectionReasons } from 'src/services/applications/applications.utils';
import {
  useGetOrganizationSettingsQuery,
  useGetRejectionTemplateSettingsQuery,
  useUpdateOrganizationSettingsMutation,
  useUpdateRejectionTemplateSettingsMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';
import { RHFTextField } from 'src/components/hook-form';
import FeatureLockedButton from 'src/components/billing/feature-locked-button';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';

export default function RejectionReasonsSettings() {
  const { t } = useTranslate();
  const theme = useTheme();

  const { canAccess } = useFeatures();

  const { organization, account } = useAuthContext();

  const { currentData: currentTemplates, isLoading: isLoadingTemplates } = useGetRejectionTemplateSettingsQuery(organization.id);

  const [updateTemplateQuestions, { isLoading: isSubmitting }] = useUpdateRejectionTemplateSettingsMutation();

  const { currentData: settingsData, isLoading: isLoadingSettings } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const [updateSettings] = useUpdateOrganizationSettingsMutation();

  const [rejectionTemplatesEditable, setRejectionTemplatesEditable] = useState(false);
  const sortedTemplates = useMemo(() => {
    if (Array.isArray(currentTemplates) && currentTemplates.length > 0) {

      const copyTemplates = [...currentTemplates];

      // order based on UserManagedRejectionReasons
      return copyTemplates.sort((a, b) => {
        const aIndex = UserManagedRejectionReasons.findIndex((reason) => reason.value === a.reason as RejectionReason);
        const bIndex = UserManagedRejectionReasons.findIndex((reason) => reason.value === b.reason as RejectionReason);
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        } if (aIndex !== -1) {
          return -1;
        } if (bIndex !== -1) {
          return 1;
        }
        return a.reason.localeCompare(b.reason);

      });

    }
    return [];
  }, [currentTemplates]);

  useEffect(() => {
    if (settingsData) {

      const rejectionTemplateSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.REJECTION_TEMPLATE_MESSAGES_EDITABLE
      );

      if (rejectionTemplateSetting) {
        setRejectionTemplatesEditable(rejectionTemplateSetting.setting_value as boolean);
      }

    }
  }, [settingsData, setRejectionTemplatesEditable]);

  const RejectionTemplatesSchema = Yup.object().shape({
    templates: Yup.array()
      .of(
        Yup.object().shape({
          reason: Yup.string().required(t('validation.required')),
          message: rejectionTemplatesEditable ? Yup.string().nullable().default('') : Yup.string().required(t('validation.required'))
        })
      )
      .required(t('validation.required'))
  });

  const defaultValues = {
    templates: sortedTemplates
  };

  const methods = useForm({
    resolver: yupResolver(RejectionTemplatesSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = methods;

  useEffect(() => {
    reset({
      templates: sortedTemplates
    });
  }, [sortedTemplates, reset]);

  const { fields } = useFieldArray({
    control,
    name: 'templates',
  });

  const onRejectionSettingsUpdate = async (value: boolean) => {

    setRejectionTemplatesEditable(value);

    try {
      const settingsToUpdate = [];

      if (!settingsData) return;

      const rejectionTemplateSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.REJECTION_TEMPLATE_MESSAGES_EDITABLE
      );

      if (rejectionTemplateSetting) {
        settingsToUpdate.push({
          id: rejectionTemplateSetting.id,
          value
        });
      }

      if (settingsToUpdate.length === 0) return;

      await updateSettings({
        organizationId: organization.id,
        settings: settingsToUpdate,
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.business_settings.update.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('admin-organization.settings.business_settings.update.default_error'), { variant: 'error' });
    }
  }

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  const rejectionTemplatesFeatureEnabled = useMemo(() => canAccess(FeatureType.REJECTION_TEMPLATES_ENABLED), [canAccess]);

  if (isLoadingTemplates) {
    return (
      <Box sx={{ width: '100%' }}>
        <Card>
          <CardContent>
            <Typography>{t('common.loading')}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const onSubmit = handleSubmit(async (data: any) => {
    try {

      await updateTemplateQuestions({
        organizationId: organization.id,
        data
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.rejection_reasons.api.update.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('admin-organization.settings.rejection_reasons.api.update.default_error'), { variant: 'error' });
    }
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="icon-park-outline:reject"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.rejection_reasons.title')}
            </Typography>
          }
        />

        <CardContent>
          <FormProvider {...methods}>
            <Stack direction="column" justifyContent="center" sx={{ mt: 2 }} gap={2}>

              <FormControlLabel
                disabled={!canEditSettings || !rejectionTemplatesFeatureEnabled}
                label={
                  <Stack gap={0} sx={{ ml: 1 }}>
                    <Typography variant="body2">
                      {t('admin-organization.settings.rejection_reasons.labels.rejection_templates_editable')}
                    </Typography>
                    <FormHelperText sx={{ mt: 0 }}>
                      {t('admin-organization.settings.rejection_reasons.labels.rejection_templates_editable_helper')}
                    </FormHelperText>
                  </Stack>
                }
                control={<Checkbox checked={rejectionTemplatesEditable} onChange={(e) => onRejectionSettingsUpdate(e.target.checked)} />}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="textSecondary">
                {t('admin-organization.settings.rejection_reasons.description')}
              </Typography>
              <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                {
                  fields.map((field, index) => (
                    <Fragment key={field.id}>
                      <Grid item xs={11} sm={4}>
                        <TextField
                          value={t(`applications.rejection.reasons.${field.reason}`)}
                          label={t('admin-organization.settings.rejection_reasons.labels.reason')}
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={11} sm={8}>
                        <RHFTextField
                          name={`templates.${index}.message`}
                          label={t('admin-organization.settings.rejection_reasons.labels.message')}
                          multiline
                          disabled={!canEditSettings || !rejectionTemplatesEditable}
                          rows={4}
                          fullWidth
                        />
                      </Grid>
                    </Fragment>
                  ))}
              </Grid>

              {
                errors.templates?.root && (
                  <Typography variant="caption" color="error">
                    {errors.templates?.root.message}
                  </Typography>
                )
              }

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>

                {
                  rejectionTemplatesFeatureEnabled ? (
                    <LoadingButton
                      type="submit"
                      onClick={onSubmit}
                      variant="contained"
                      loading={isSubmitting}
                      disabled={!isValid || !canEditSettings}
                    >
                      {t('common.update')}
                    </LoadingButton>
                  ) : (
                    <FeatureLockedButton
                      label={t(`common.subscription_required`)}
                      variant="contained"
                    />
                  )
                }

              </Box>
            </Stack>
          </FormProvider>

        </CardContent>
      </Card>
    </Box>
  )
}